/**
 * Shared color utilities for team badges and user avatars.
 * These colors are used consistently across the boards that show people.
 */

export const TEAM_BADGE_COLORS = [
  '#2E7D4A', // green
  '#2E9E9E', // teal
  '#EFA52A', // amber
  '#C17F24', // warm amber
  '#1C6B6B', // deep teal
  '#C43D3D', // red
  '#7A756D', // neutral
  '#B87A1A', // orange
  '#5B6ABF', // indigo
  '#7C3E8F', // purple
  '#3A7CA5', // steel blue
  '#6B8E23', // olive
  '#D4764E', // terracotta
  '#2B8C6F', // emerald
  '#9E6B4A', // brown
  '#4A90A4', // ocean
];

/**
 * Generate a consistent color from a string (name or email).
 * Uses a simple hash function for deterministic color assignment.
 */
export function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32-bit integer
  }

  return TEAM_BADGE_COLORS[Math.abs(hash) % TEAM_BADGE_COLORS.length];
}

/**
 * A person's stable identity colour, keyed name-first with an id fallback, so
 * the SAME person reads as the SAME colour on every surface that shows them
 * (e.g. the allocation board). One keying rule, one place — so the surfaces
 * can never silently drift apart.
 */
export function userColor(name: string | null | undefined, id: string): string {
  return stringToColor(name || id);
}

/**
 * Extract initials from a name or email.
 * Handles empty strings and ensures we don't exceed array bounds.
 */
export function getInitials(name: string | null, email: string): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2 && parts[0].length > 0 && parts[parts.length - 1].length > 0) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    // Single word name or multi-word with short parts
    const trimmed = name.trim();
    if (trimmed.length >= 2) {
      return trimmed.slice(0, 2).toUpperCase();
    }
    if (trimmed.length === 1) {
      return trimmed.toUpperCase();
    }
  }

  // Fall back to email
  if (email.length >= 2) {
    return email.slice(0, 2).toUpperCase();
  }
  if (email.length === 1) {
    return email.toUpperCase();
  }

  // Absolute fallback (should never happen with valid emails)
  return '??';
}
